import {User} from '@models/user';
import {ActivatedRoute, Router} from '@angular/router';
import {Injectable} from '@angular/core';
import {AccountServiceResponseInterface} from '@interfaces/accountServiceResponse.interface';
import {Address} from '@models/address';
import {UserInterface} from '@interfaces/user.interface';
import {PermissionFacade} from '@facades/permission/permissionFacade';
import {AuthenticatedSuccessAction, UpdateAuthUser} from '@store/auth/actions';
import {Store} from '@node_modules/@ngrx/store';
import * as AuthenticateReducer from '@store/auth/reducers';
import {AccountService} from '@services/account/account.service';
import {AuthService} from '@services/auth/auth.service';

@Injectable()
export class AuthFacade {
  private static prefix = 'auth_schools_';

  private notAuthnticatePages = [
    '/auth/register',
    '/auth/login',
    '/auth/resetPassword',
    '/auth/setup'
  ];

  public constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<AuthenticateReducer.AuthState>,
    private accountService: AccountService,
    private authService: AuthService,
  ) {
  }

  static getUser = () => {
    const userJsn = localStorage.getItem(AuthFacade.prefix + 'user');
    if (typeof userJsn !== 'undefined' && userJsn !== 'undefined') {
      return JSON.parse(localStorage.getItem(AuthFacade.prefix + 'user'));
    }
  }

  static setToken = (token: string, prefix = AuthFacade.prefix) => {
    localStorage.setItem(prefix + 'token', token);
  }

  static getToken = (prefix = AuthFacade.prefix): string => {
    return localStorage.getItem(prefix + 'token');
  }

  static setUser = (user: User) => {
    localStorage.setItem(AuthFacade.prefix + 'user', JSON.stringify(user));
  }

  static setAuthStatus = (status: boolean) => {
    localStorage.setItem(AuthFacade.prefix + 'status', status.toString());
  }

  static getAuthStatus = () => {
    return localStorage.getItem(AuthFacade.prefix + 'status') === 'true';
  }

  checkAuthStatusAndRedirect(page = null) {
    page = page ? page : this.router.url.split('?')[0];

    const redirectToParam = this.route.snapshot.queryParams['redirectTo'];
    const redirectTo = redirectToParam ? redirectToParam : '/dashboard';

    setTimeout(() => {
      if (AuthFacade.getAuthStatus()) {
        this.router.navigate([redirectTo]);
      } else if (!AuthFacade.getAuthStatus() && !this.notAuthnticatePages.includes(page)) {
        this.router.navigate(['/auth/login']);
      }
    }, 45);
  }

  pageNeedAuth(page = null) {
    const url = page ? page : this.router.url.split('?')[0];
    console.log(url);
    console.log(this.notAuthnticatePages.includes(url));
    return !this.notAuthnticatePages.includes(url);
  }

  endSession(redirect: boolean) {
    const token = AuthFacade.getToken();

    if (token) {
      this.authService.logout(token).subscribe(
        () => this.clearLocalSession(redirect),
        () => this.clearLocalSession(redirect)
      );
      return;
    }

    this.clearLocalSession(redirect);
  }

  private clearLocalSession(redirect: boolean) {
    AuthFacade.setUser(new User());
    AuthFacade.setToken('');
    AuthFacade.setAuthStatus(false);

    if (redirect === true) {
      const uri = window.location.origin + '/#/auth/login';
      window.location.href = uri;
    }
  }

  public createUser(response: AccountServiceResponseInterface, permissions = {}): User {
    return new User({
      id: response.data.id,
      name: response.data.name,
      login_name: response.data.login_name,
      not_read_messages: response.data.not_read_messages,
      subscriptions: response.data.subscriptions,
      first_name: response.data.first_name,
      default_currency: response.data.default_currency,
      last_name: response.data.last_name,
      programs_id: response.data.programs_id,
      classes_id: response.data.classes_id,
      id_payment_method: response.data.id_payment_method,
      email: response.data.email,
      address: response.data.address ? response.data.address : new Address(),
      family: response.data.family,
      parent_id: response.data.parent_id,
      notAdmin: (response.data.role as string) !== 'admin' && (response.data.role as string) !== 'super_admin',
      avatar: response.data.avatar,
      role: response.data.role,
      emailVerified: response.data.emailVerified,
      phoneNumberVerified: response.data.phoneNumberVerified,
      registerNotComplete: response.data.registerNotComplete,
      permissions: permissions,
      phone: response.data.phone,
      countryCode: response.data.countryCode,
      current_lesson: response.data.current_lesson,
      schools: response.data.schools,
      paymentSettingVerified: response.data.paymentSettingVerified,
      stripe_status: response.data.stripe_status,
      stripe_email: response.data.stripe_email,
      usedAuthSocial: response.data.usedAuthSocial ? response.data.usedAuthSocial : false,
      hasProgram: response.data.hasProgram ? response.data.hasProgram : false,
      stripeCustomerId: response.data.stripeCustomerId,
      stripeAccountId: response.data.stripeAccountId,
      stripePublicKey: response.data.stripePublicKey,
      yearAttendanceFromApi: response.data.yearAttendanceFromApi,
      card_digits: response.data.card_digits,
      systemSubscriptionStatus: response.data.systemSubscriptionStatus,
      birth_date: response.data.birth_date,
      level: response.data.level,
    });
  }

  fetchUser() {
    this.accountService
      .getAccount()
      .subscribe(
        (response: AccountServiceResponseInterface) => {
          this.saveAccount(response);
        },
        error => {
          console.log('fetch user error', error);
        }
      );
  }

  saveAccount(response: AccountServiceResponseInterface): void {
    const permissions = PermissionFacade.groupByModelName(response.data.permissions);
    const user = this.createUser(response, permissions);
    AuthFacade.setUser(user);
    this.store.dispatch(new UpdateAuthUser(user));
    this.store.dispatch(new AuthenticatedSuccessAction({authenticated: true, user: user}));
  }

  updateSystemSubscriptionStatus(status): void {
    if (!status) {
      return;
    }
    const authUser = AuthFacade.getUser();
    authUser.systemSubscriptionStatus = status;
    AuthFacade.setUser(authUser);
  }
}
